import React, { useMemo, useContext } from 'react';
import { safeJsonParse } from '../../../../../utils/jsonRepairUtils';
import {
  AttackPathView,
  ATTACK_PATH_CONTAINER_HEIGHT,
} from '../AttackPathView';
import { AttackPathLookupContext } from '../AttackPathLookupContext';
import { useDebouncedValue } from './useDebouncedValue';
import '../ExtendedMarkdown.css';

import { useTranslation } from 'react-i18next';

interface MarkdownAttackPathProps {
  code: string;
}

function AttackPathLoadingSkeleton() {
  const { t } = useTranslation('pleri/chat');
  return (
    <div
      className="relative isolate z-10 my-4 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700"
      style={{ height: ATTACK_PATH_CONTAINER_HEIGHT }}
      role="status"
      aria-live="polite"
      aria-label={t('loadingAttackPathGraph')}
    >
      {/* Solid layer: fully hides graph/code behind until loading is done */}
      <div
        className="absolute inset-0 z-[1] bg-gray-100 dark:bg-gray-800"
        aria-hidden
      />
      {/* Shimmer animation on top of solid layer */}
      <div
        className="attack-path-loading-shimmer absolute inset-0 z-[2] w-[70%] bg-gradient-to-r from-transparent via-white to-transparent dark:via-gray-900"
        aria-hidden
      />
    </div>
  );
}

/** Parses attack path block: { findingId }. Returns findingId to look up. */
function extractFindingId(parsed: unknown): string | null {
  if (!parsed || typeof parsed !== 'object') {
    return null;
  }
  const obj = parsed as Record<string, unknown>;
  const id =
    typeof obj.findingId === 'string' && obj.findingId.trim() !== ''
      ? obj.findingId.trim()
      : null;
  return id;
}

export const MarkdownAttackPath: React.FC<MarkdownAttackPathProps> = ({
  code,
}) => {
  const { t } = useTranslation('pleri/chat');
  const debouncedCode = useDebouncedValue(code, 300, 500);
  const { getAttackPathsForFindingId } = useContext(AttackPathLookupContext);

  const parsed = useMemo(() => {
    const trimmed = debouncedCode.trim();
    if (!trimmed) {
      return { kind: 'empty' as const };
    }
    let value: unknown;
    try {
      value = safeJsonParse(trimmed, 'attack path markdown');
    } catch {
      return { kind: 'error' as const, error: 'Invalid JSON' };
    }
    const findingId = extractFindingId(value);
    if (findingId) {
      return { kind: 'findingId' as const, findingId };
    }
    return {
      kind: 'error' as const,
      error: 'Expected { findingId }',
    };
  }, [debouncedCode]);

  const attackPathsFromLookup =
    parsed.kind === 'findingId'
      ? getAttackPathsForFindingId(parsed.findingId)
      : null;

  const codeLooksComplete = code.trim().endsWith('}');
  const isStreaming =
    code.trim() !== '' && (code !== debouncedCode || !codeLooksComplete);

  if (isStreaming) {
    return <AttackPathLoadingSkeleton />;
  }

  if (parsed.kind === 'error') {
    return (
      <p className="my-4 text-sm text-gray-500 dark:text-gray-400">
        {t('attackPath', { error: parsed.error })}
      </p>
    );
  }

  if (parsed.kind === 'empty') {
    return null;
  }

  // parsed.kind === 'findingId'
  if (attackPathsFromLookup) {
    return <AttackPathView attackPaths={attackPathsFromLookup} />;
  }
  return (
    <p className="my-4 text-sm text-gray-500 dark:text-gray-400">
      {t('attackPathGraphNotAvailableInThis')}
    </p>
  );
};
